/**
 * Mobile Structured Logging Utility.
 *
 * Provides a structured JSON logger for mobile observability.
 * Logs are written to persistent storage and can be flushed to
 * the backend for debugging and analytics.
 *
 * Levels: debug, info, warn, error
 * Each log entry includes: timestamp, level, event, payload, sessionId, platform
 */
import { apiClient } from '../../shared/api-client';
import { getPersistentJson, setPersistentItem } from '../../vrs-auth/storage';
import { redactDiagnosticRecord, redactDiagnosticText } from './diagnosticRedaction';
const LOG_STORAGE_KEY = 'vrs_mobile_logs';
const MAX_LOG_ENTRIES = 500;
const UPLOAD_TIMEOUT_MS = 15000;
let pendingUpload = null;
let clearRevision = 0;
const LOG_LEVEL_PRIORITY = {
    debug: 0,
    info: 1,
    warn: 2,
    error: 3
};
let minLogLevel = __DEV__ ? 'debug' : 'info';
const getPlatform = () => {
    if (typeof navigator === 'undefined') {
        return 'unknown';
    }
    return navigator.product === 'ReactNative' ? 'native' : 'web';
};
const getSessionId = () => {
    const stored = getPersistentJson('vrs_session');
    if (typeof stored?.sessionId === 'string' && stored.sessionId) {
        return stored.sessionId;
    }
    const newSession = {
        sessionId: `sess-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        createdAt: new Date().toISOString()
    };
    setPersistentItem('vrs_session', JSON.stringify(newSession));
    return newSession.sessionId;
};
const getStoredLogs = () => {
    const stored = getPersistentJson(LOG_STORAGE_KEY);
    if (stored === null || stored === undefined) {
        return [];
    }
    // Cached diagnostics are untrusted and may predate credential redaction.
    const logs = [];
    for (const candidate of Array.isArray(stored) ? stored.slice(-MAX_LOG_ENTRIES) : []) {
        if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) {
            continue;
        }
        const row = candidate;
        if (typeof row.timestamp !== 'string' || typeof row.event !== 'string'
            || typeof row.platform !== 'string'
            || typeof row.level !== 'string'
            || !['debug', 'info', 'warn', 'error'].includes(row.level)) {
            continue;
        }
        logs.push({
            timestamp: redactDiagnosticText(row.timestamp),
            level: row.level,
            event: redactDiagnosticText(row.event),
            payload: row.payload && typeof row.payload === 'object' && !Array.isArray(row.payload)
                ? redactDiagnosticRecord(row.payload) : undefined,
            sessionId: typeof row.sessionId === 'string' ? redactDiagnosticText(row.sessionId) : undefined,
            platform: redactDiagnosticText(row.platform)
        });
    }
    // Repair once, before any display/export/upload (including a failed upload).
    if (JSON.stringify(stored) !== JSON.stringify(logs)) {
        setPersistentItem(LOG_STORAGE_KEY, JSON.stringify(logs));
    }
    return logs;
};
const persistLogs = (logs) => {
    const trimmed = logs.slice(-MAX_LOG_ENTRIES);
    setPersistentItem(LOG_STORAGE_KEY, JSON.stringify(trimmed));
};
const mobileLog = (level, event, payload, options) => {
    if (LOG_LEVEL_PRIORITY[level] < LOG_LEVEL_PRIORITY[minLogLevel]) {
        return;
    }
    const entry = {
        timestamp: new Date().toISOString(),
        level,
        event: redactDiagnosticText(event),
        payload: payload ? redactDiagnosticRecord(payload) : undefined,
        sessionId: redactDiagnosticText(getSessionId()),
        platform: getPlatform()
    };
    // Console output for development
    if (__DEV__ && options?.console !== false) {
        const prefix = `[mobile:${level}]`;
        const tag = entry.event;
        switch (level) {
            case 'error':
                if (getPlatform() === 'web') {
                    console.error(prefix, tag, entry.payload);
                }
                else {
                    console.warn(prefix, tag, entry.payload);
                }
                break;
            case 'warn':
                console.warn(prefix, tag, entry.payload);
                break;
            default:
                console.log(prefix, tag, entry.payload);
        }
    }
    // Persist to storage
    const logs = getStoredLogs();
    logs.push(entry);
    persistLogs(logs);
    if (pendingUpload) {
        pendingUpload.appended = [...pendingUpload.appended, entry].slice(-MAX_LOG_ENTRIES);
    }
};
export const setMinLogLevel = (level) => {
    minLogLevel = level;
};
export const getMobileLogs = (options) => {
    let logs = getStoredLogs();
    if (options?.level) {
        const minPriority = LOG_LEVEL_PRIORITY[options.level];
        logs = logs.filter(l => LOG_LEVEL_PRIORITY[l.level] >= minPriority);
    }
    if (options?.event) {
        logs = logs.filter(l => l.event === options.event);
    }
    if (options?.limit) {
        logs = logs.slice(-options.limit);
    }
    return logs;
};
export const clearMobileLogs = () => {
    clearRevision++;
    setPersistentItem(LOG_STORAGE_KEY, JSON.stringify([]));
};
export const flushLogs = () => {
    const logs = getStoredLogs();
    clearMobileLogs();
    return logs;
};
/** Coalesce callers and remove only the confirmed snapshot, retaining later entries. */
export const flushMobileLogsToBackend = () => {
    if (pendingUpload)
        return pendingUpload.result;
    const logs = getStoredLogs();
    if (!logs.length)
        return Promise.resolve({ sent: 0, remaining: 0 });
    const startedRevision = clearRevision;
    const work = { result: Promise.resolve({ sent: 0, remaining: 0 }), appended: [] };
    pendingUpload = work;
    work.result = new Promise(resolve => {
        const controller = new AbortController();
        let settled = false;
        const finish = (sent, error) => {
            if (settled)
                return;
            settled = true;
            clearTimeout(timer);
            controller.abort();
            if (pendingUpload === work)
                pendingUpload = null;
            resolve({ sent, remaining: getStoredLogs().length, ...(error ? { error } : {}) });
        };
        const unconfirmed = 'Upload not confirmed. Your diagnostics are kept. Try again.';
        const timer = setTimeout(() => finish(0, unconfirmed), UPLOAD_TIMEOUT_MS);
        void Promise.resolve().then(() => settled ? undefined : apiClient.post('/api/mobile/logs', { logs }, {
            signal: controller.signal, retryAuth: false
        })).then(response => {
            if (settled || !response)
                return;
            if (response.status === 401 || response.status === 403) {
                finish(0, 'Sign in again to send diagnostics.');
                return;
            }
            const data = response.data;
            if (response.error || typeof response.status !== 'number' || response.status < 200 || response.status >= 300
                || !data || typeof data !== 'object' || Array.isArray(data)
                || data.accepted !== logs.length) {
                finish(0, unconfirmed);
                return;
            }
            // A clear/replacement during upload must never consume new, even identical, entries.
            const expected = [...logs, ...work.appended].slice(-MAX_LOG_ENTRIES);
            if (clearRevision === startedRevision && JSON.stringify(getStoredLogs()) === JSON.stringify(expected)) {
                persistLogs(work.appended);
            }
            finish(logs.length);
        }).catch(() => finish(0, unconfirmed));
    });
    return work.result;
};
export { mobileLog };
